import { CoverageOption, InlandMarineEquipment as Tool } from "@deathstar/types/northstar";
import { EquipmentRow } from "@deathstar/types/waypoint";
import { ActionButton, DataTable, fuzzyFilter, useSnackbar } from "@deathstar/ui";
import { PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { animated, useSpring } from "react-spring";
import { useAccountId } from "../../api/useAccountId";
import Loader from "../../components/loader/Loader";
import {
    getPendingEquipmentChanges,
    hasPendingRemoval,
    getPendingEquipmentPolicyChangePriority,
    getPendingPolicyChangeRowClass,
    isPendingPolicyAdditionRow,
    usePendingPolicyChanges,
    withPendingEquipmentAdditions,
} from "../pending-policy-changes/PendingPolicyChanges";
import EquipmentColumns, { EquipmentTableMeta } from "./EquipmentColumns";
import { NewUnitDialog } from "./NewUnitDialog";

const AnimatedDataTable = animated(DataTable<EquipmentRow<Tool>>);

export default function ToolsTable({ data }: { data: EquipmentRow<Tool>[] | undefined }) {
    const accountId = useAccountId();
    const [adding, setAdding] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);
    const pendingPolicyChanges = usePendingPolicyChanges();

    const memoizedData = useMemo(() => data || [], [data]);
    const tableData = useMemo(
        () => withPendingEquipmentAdditions(memoizedData, pendingPolicyChanges.changes, accountId!, "tools"),
        [accountId, memoizedData, pendingPolicyChanges.changes]
    );

    const spring = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        pause: !data,
    });

    const sortingState = useMemo(() => {
        return [{ id: "pending", desc: true }, ...sorting];
    }, [sorting]);

    const meta: Pick<EquipmentTableMeta, "isPendingRemoval"> = useMemo(
        () => ({
            isPendingRemoval(row: { accountId: string; id: number }) {
                return hasPendingRemoval(getPendingEquipmentChanges(pendingPolicyChanges.changes, row.accountId, "tools", row.id));
            },
        }),
        [pendingPolicyChanges.changes]
    );

    const columns = useMemo(
        () =>
            [
                EquipmentColumns.mobileToolCell,
                EquipmentColumns.toolBrowserPending,
                {
                    ...EquipmentColumns.pending,
                    accessorFn: (row: EquipmentRow<Tool>) =>
                        getPendingEquipmentPolicyChangePriority(pendingPolicyChanges.changes, row, "tools"),
                },
                EquipmentColumns.upcomingDate,
                EquipmentColumns.description,
                EquipmentColumns.year,
                EquipmentColumns.make,
                EquipmentColumns.model,
                EquipmentColumns.serialNumber,
                EquipmentColumns.value,
                EquipmentColumns.coverages,
                EquipmentColumns.additionalInterests,
                EquipmentColumns.toolActions,
            ] as ColumnDef<EquipmentRow<Tool>>[],
        [pendingPolicyChanges.changes]
    );

    const table = useReactTable({
        data: tableData,
        columns,
        meta,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getColumnCanGlobalFilter: () => true,
        globalFilterFn: fuzzyFilter,
        state: {
            sorting: sortingState,
        },
        initialState: {
            pagination: {
                pageSize: 30,
            },
        },
        onSortingChange: setSorting,
    });

    const downloadCSV = useMutation({
        mutationFn: async () => {
            if (memoizedData.length === 0) {
                useSnackbar.add("No data to export", { variant: "info" });
                return;
            }

            const rows: string[] = [];
            memoizedData.forEach((row) => {
                rows.push(
                    [
                        row.description,
                        row.year,
                        row.make,
                        row.model,
                        row.serialNumber,
                        row.policyAcv ? "ACV" : row.policyValue,
                        row.coverages
                            ?.filter((c) => !c.isUpcoming)
                            .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
                            .join(",") || "",
                    ]
                        .map((x) => `"${x ?? ""}"`)
                        .join(",")
                );
            });

            const csv = [["Description", "Year", "Make", "Model", "S/N", "Value", "Coverage"].join(","), ...rows].join("\n");

            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.download = "tools.csv";
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
    });

    return (
        <div>
            {data ? (
                <>
                    <AnimatedDataTable
                        style={spring}
                        table={table}
                        getRowClassName={(row) =>
                            isPendingPolicyAdditionRow(row)
                                ? "bg-emerald-100/80"
                                : getPendingPolicyChangeRowClass(
                                      getPendingEquipmentChanges(pendingPolicyChanges.changes, row.accountId, "tools", row.id)
                                  )
                        }
                        headerRow={
                            <div className="flex flex-wrap items-end justify-end gap-4">
                                <ActionButton disabled={downloadCSV.isPending} onClick={() => downloadCSV.mutate()}>
                                    {downloadCSV.isPending ? <CircularProgress size="1rem" /> : <TableCellsIcon className="h-5 w-5" />}
                                    Export
                                </ActionButton>
                                <ActionButton onClick={() => setAdding(true)}>
                                    <PlusIcon className="h-5 w-5" />
                                    Add tool
                                </ActionButton>
                                <DataTable.Search table={table} />
                            </div>
                        }
                    />
                    <NewUnitDialog
                        open={adding}
                        onClose={() => setAdding(false)}
                        unitType="tools"
                        defaultValues={{ atDate: moment().format("YYYY-MM-DD") }}
                        deferPolicyChanges
                    />
                </>
            ) : (
                <Loader />
            )}
        </div>
    );
}
